import type {
  CompanyMergeFieldKey,
  CompanyMergeSide,
  CompanyMergeXeroChoice,
} from '@repo/schema/company-merge'

/**
 * Centralized data-testid constants for E2E testing (BugBug)
 *
 * Naming Convention: [feature]-[component]-[element]-[variant]
 *
 * Rules:
 * 1. Always prefix with feature domain
 * 2. Keep breakpoint suffixes (-xs, -sm, -lg, -3xl) for responsive-grid components
 * 3. Use consistent dynamic patterns with entity.slug
 * 4. Form inputs include field context
 * 5. Actions include verb (delete-confirm, not confirm-delete)
 */

export const TEST_IDS = {
  // ============================================
  // App Layout
  // ============================================
  app: {
    homeContainer: 'home',
  },

  // ============================================
  // Broker Management
  // ============================================
  broker: {
    details: {
      invoicing: {
        currencySelect: 'broker-details-invoicing-currency-select',
      },
    },
  },

  // ============================================
  // Cargo Details
  // ============================================
  cargo: {
    packaging: {
      input: 'packaging-input',
      minusButton: 'packaging-input-minus',
      typeSelect: 'packaging-type-select',
    },
    temperature: {
      input: 'temperature-input',
      minusButton: 'temperature-input-minus',
      plusButton: 'temperature-input-plus',
    },
    trailerTypeSelect: 'trailer-type-select',
    weight: {
      input: 'weight-input',
      minusButton: 'weight-input-minus',
      plusButton: 'weight-input-plus',
      unitSelect: 'weight-unit-select',
    },
  },
  // ============================================
  // Chat
  // ============================================
  chat: {
    comment: {
      actionsTrigger: 'chat-comment-actions-trigger',
      copy: 'chat-comment-copy',
      deleteCancel: 'chat-comment-delete-cancel',
      deleteConfirm: 'chat-comment-delete-confirm',
      deleteTrigger: 'chat-comment-delete-trigger',
      item: (id: number) => `chat-comment-item-${id}`,
    },
    input: 'chat-input',
    sendButton: 'send-message-button',
    shipperTrigger: 'chat-with-shipper-trigger',
  },

  // ============================================
  // Command Menu (Cmd+K)
  // ============================================
  cmdk: {
    copyLoadDetails: {
      clearAll: (group: string) => `cmdk-copy-load-details-clear-all-${group}`,
      field: (field: string) => `cmdk-copy-load-details-field-${field}`,
      fieldGroup: (group: string) => `cmdk-copy-load-details-field-group-${group}`,
      outputStyle: (style: string) => `cmdk-copy-load-details-output-style-${style}`,
      outputStylePage: 'cmdk-copy-load-details-output-style-page',
      preview: 'cmdk-copy-load-details-preview',
      selectAll: (group: string) => `cmdk-copy-load-details-select-all-${group}`,
      settings: 'cmdk-copy-load-details-settings',
    },
    godModeToggle: 'cmdk-god-mode-toggle',
    input: 'cmdk-input',
    navigation: {
      hauliers: 'cmdk-navigation-hauliers',
      loads: 'cmdk-navigation-loads',
      shippers: 'cmdk-navigation-shippers',
      users: 'cmdk-navigation-users',
    },
  },

  // ============================================
  // Company Management (Shippers/Hauliers)
  // ============================================
  company: {
    commandInput: 'company-command-input',
    form: {
      addressInput: 'company-address-input',
      cityInput: 'company-city-input',
      countrySelect: 'company-country-select',
      legalNameInput: 'company-legal-name-input',
      notesTextarea: 'company-form-notes-textarea',
      phoneInput: 'company-phone-input',
      postcodeInput: 'company-postcode-input',
      registrationNumberInput: 'company-registration-number-input',
      shortNameInput: 'company-form-short-name-input',
      vatInput: 'company-vat-input',
    },
    inputTrigger: 'company-input-trigger',
    menu: {
      dropdownTrigger: 'status-bar-dropdown-trigger',
      merge: 'company-menu-merge',
    },
    merge: {
      back: 'company-merge-back',
      cancel: 'company-merge-cancel',
      confirm: 'company-merge-confirm',
      contactOption: (id: number) => `company-merge-contact-${id}`,
      container: 'company-merge-wizard',
      continue: 'company-merge-continue',
      fieldOption: (key: CompanyMergeFieldKey, side: CompanyMergeSide) =>
        `company-merge-field-${key}-${side}`,
      primaryOption: (id: number) => `company-merge-primary-${id}`,
      removeCompany: (id: number) => `company-merge-remove-${id}`,
      successAlert: 'company-merge-success-alert',
      xeroOption: (choice: CompanyMergeXeroChoice) => `company-merge-xero-${choice}`,
    },
    tagTypeArchiveDialog: {
      cancelButton: 'company-tag-type-archive-cancel-button',
      confirmButton: 'company-tag-type-archive-confirm-button',
    },
    xero: {
      merged: 'company-xero-merged',
      notSynced: 'company-xero-not-synced',
      status: 'company-xero-status',
    },
  },

  // ============================================
  // Company Locations
  // ============================================
  companyLocation: {
    addButton: 'company-location-add-button',
    drawer: {
      closeButton: 'company-location-drawer-close-button',
      title: 'company-location-drawer-title',
    },
    form: {
      addressInput: 'company-location-form-address-input',
      addressSecondLineInput: 'company-location-form-address-second-line-input',
      cancelButton: 'company-location-form-cancel-button',
      cityInput: 'company-location-form-city-input',
      countrySelect: 'company-location-form-country-select',
      hideGeolocationCheckbox: 'company-location-form-hide-geolocation-checkbox',
      instructionsTextarea: 'company-location-form-instructions-textarea',
      latInput: 'company-location-form-lat-input',
      lngInput: 'company-location-form-lng-input',
      manualButton: 'company-location-form-manual-button',
      nameInput: 'company-location-form-name-input',
      postcodeInput: 'company-location-form-postcode-input',
      radiusInput: 'company-location-form-radius-input',
      searchInput: 'company-location-form-search-input',
      submitButton: 'company-location-form-submit-button',
    },
  },

  // ============================================
  // Company Contacts
  // ============================================
  contact: {
    actions: {
      deleteButton: 'contact-actions-delete-button',
      editButton: 'contact-actions-edit-button',
    },
    addButton: 'contact-add-button',
    deleteDialog: {
      cancelButton: 'contact-delete-cancel-button',
      confirmButton: 'contact-delete-confirm-button',
    },
    drawer: {
      closeButton: 'contact-drawer-close-button',
      title: 'contact-drawer-title',
    },
    form: {
      cancelButton: 'contact-form-cancel-button',
      emailInput: 'contact-form-email-input',
      hxInput: 'contact-form-hx-input',
      lastNameInput: 'contact-form-last-name-input',
      methodInput: 'contact-form-method-input',
      nameInput: 'contact-form-name-input',
      nicknameInput: 'contact-form-nickname-input',
      notesTextarea: 'contact-form-notes-textarea',
      phoneInput: 'contact-form-phone-input',
      styleInput: 'contact-form-style-input',
      submitButton: 'contact-form-submit-button',
      timocomIdInput: 'contact-form-timocom-id-input',
      transIdInput: 'contact-form-trans-id-input',
      typeOption: (type: string) => `contact-form-type-option-${type}`,
      typeSelect: 'contact-form-type-select',
      whatsappInput: 'contact-form-whatsapp-input',
    },
  },

  // ============================================
  // Setup Form Management
  // ============================================
  customerSetupForm: {
    deleteDialog: {
      cancelButton: 'customer-setup-form-delete-cancel-button',
      confirmButton: 'customer-setup-form-delete-confirm-button',
    },
    step1: {
      companyPhoneInput: 'setup-form-step1-company-phone-input',
    },
    step2: {
      accountsPhoneInput: 'setup-form-step2-accounts-phone-input',
      transportPhoneInput: 'setup-form-step2-transport-phone-input',
    },
    step3: {
      primaryReferencePhoneInput: 'setup-form-step3-primary-reference-phone-input',
      secondaryReferencePhoneInput: 'setup-form-step3-secondary-reference-phone-input',
    },
    step4: {
      userEmailInput: 'user_email',
      userLastNameInput: 'user_last_name',
      userNameInput: 'user_name',
      userPhoneInput: 'setup-form-step4-user-phone-input',
    },
  },

  // ============================================
  // Generic Form Components (packages/form)
  // ============================================
  form: {
    autoResizeTextarea: 'form-auto-resize-textarea',
    phoneInput: 'form-phone-input',
    podDocuments: {
      internalUploadInput: 'form-pod-documents-internal-upload-input',
      readyForCustomerUploadInput: 'form-pod-documents-ready-for-customer-upload-input',
      uploadAcceptedResult: 'form-pod-documents-upload-accepted-result',
      uploadRejectedResult: 'form-pod-documents-upload-rejected-result',
    },
    podRequirement: {
      descriptionInput: 'form-pod-requirement-description-input',
      select: 'form-pod-requirement-select-trigger',
    },
    tagDeleteButton: (slug: string) => `form-tag-delete-button-${slug}`,
    textInput: 'form-text-input',
  },

  // ============================================
  // Haulier Management
  // ============================================
  haulier: {
    addCompanyButton: 'haulier-add-company-button',
    addCompanyForm: {
      addressInput: 'haulier-add-company-address-input',
      carrierCheckbox: 'haulier-add-company-carrier-checkbox',
      cityInput: 'haulier-add-company-city-input',
      countrySelect: 'haulier-add-company-country-select',
      emailInput: 'haulier-add-company-email-input',
      forwarderCheckbox: 'haulier-add-company-forwarder-checkbox',
      phoneInput: 'haulier-add-company-phone-input',
      postcodeInput: 'haulier-add-company-postcode-input',
      regionInput: 'haulier-add-company-region-input',
      shortTitleInput: 'haulier-add-company-short-title-input',
      sourceSelect: 'haulier-add-company-source-select',
      submitButton: 'haulier-add-company-submit-button',
      titleInput: 'haulier-add-company-title-input',
    },
    details: {
      emailInput: 'haulier-details-email-input',
      hubspotInput: 'haulier-details-hubspot-input',
      invoicing: {
        billingAddressInput: 'haulier-details-invoicing-billing-address-input',
        billingAddressSecondLineInput:
          'haulier-details-invoicing-billing-address-second-line-input',
        billingCityInput: 'haulier-details-invoicing-billing-city-input',
        billingPostcodeInput: 'haulier-details-invoicing-billing-postcode-input',
        currencySelect: 'haulier-details-invoicing-currency-select',
        emailInput: 'haulier-details-invoicing-email-input',
        notesTextarea: 'haulier-details-invoicing-notes-textarea',
        phoneInput: 'haulier-details-invoicing-phone-input',
      },
      notesTextarea: 'haulier-details-notes-textarea',
      websiteInput: 'haulier-details-website-input',
    },
    summary: {
      profit: 'summary-profit',
    },
  },

  // ============================================
  // Load Management
  // ============================================
  load: {
    assignToMeButton: 'load-assign-to-me-button',
    bulkCancelModal: {
      container: 'load-bulk-cancel-modal',
      notesTextarea: 'load-bulk-cancel-notes',
      reasonOption: (slug: string) => `load-bulk-cancel-reason-${slug}`,
      reasonSelect: 'load-bulk-cancel-reason-select',
    },
    cancelModal: {
      container: 'load-cancel-modal',
      notesTextarea: 'load-cancel-notes',
      reasonOption: (slug: string) => `load-cancel-reason-${slug}`,
      reasonSelect: 'load-cancel-reason-select',
    },
    changeCustomer: {
      container: 'load-change-customer-modal',
      diffRow: (key: string) => `load-change-customer-diff-${key}`,
      keptRow: (key: string) => `load-change-customer-kept-${key}`,
      locationRow: (id: number) => `load-change-customer-location-${id}`,
      next: 'load-change-customer-next',
      save: 'load-change-customer-save',
      strategyOption: (section: string, strategy: string) =>
        `load-change-customer-${section}-${strategy}`,
      warning: (code: string) => `load-change-customer-warning-${code}`,
    },
    changeEndCustomer: {
      container: 'load-change-end-customer-modal',
      locationRow: (id: number) => `load-change-end-customer-location-${id}`,
      save: 'load-change-end-customer-save',
    },
    completedModal: {
      container: 'load-completed-modal',
      notesTextarea: 'load-completed-notes',
      reasonOption: (slug: string) => `load-completed-reason-${slug}`,
      reasonSelect: 'load-completed-reason-select',
    },
    container: 'load-detail-container',
    duplicateDialog: 'load-duplicate-dialog',
    excelIdInput: 'load-excel-id-input',
    flag: {
      banner: (slug: string) => `load-flag-banner-${slug}`,
      listIcon: 'load-list-row-flag-icon',
      listIssueIcon: 'load-list-row-issue-icon',
      menu: {
        add: 'load-flag-menu-add',
        remove: 'load-flag-menu-remove',
      },
      sheet: {
        cancel: 'load-flag-sheet-cancel',
        notes: 'load-flag-sheet-notes',
        submit: 'load-flag-sheet-submit',
      },
    },
    historyItem: 'load-history-item',
    invoiceSheet: {
      missingVatWarning: 'load-invoice-sheet-missing-vat-warning',
      submitButton: 'load-invoice-sheet-submit',
      taxRate: 'load-invoice-sheet-tax-rate',
    },
    issueModal: {
      container: 'load-issue-modal',
      notesTextarea: 'load-issue-notes',
      reasonOption: (slug: string) => `load-issue-reason-${slug}`,
      reasonSelect: 'load-issue-reason-select',
    },
    list: {
      loadingEgg: 'load-list-loading-egg',
    },
    menu: {
      copyDetails: 'load-menu-copy-details',
      duplicate: 'load-menu-duplicate',
    },
    newLoad: {
      copyDetailsOnSubmit: 'new-load-copy-details-on-submit',
      customerRateCurrency: 'new-load-customer-rate-currency',
      customerRateInput: 'new-load-customer-rate-input',
      customerRateNote: 'new-load-customer-rate-note',
    },
    notesTextarea: 'notes-textarea',
    orderConfirmation: {
      carrierField: 'load-order-confirmation-carrier-field',
      field: {
        generate: 'load-order-confirmation-field-generate',
        upload: 'load-order-confirmation-field-upload',
        uploadInput: 'load-order-confirmation-field-upload-input',
      },
      menu: {
        delete: 'load-order-confirmation-menu-delete',
        download: 'load-order-confirmation-menu-download',
      },
      row: 'load-order-confirmation-row',
      sheet: {
        generate: 'load-order-confirmation-sheet-generate',
        upload: 'load-order-confirmation-sheet-upload',
        uploadInput: 'load-order-confirmation-sheet-upload-input',
      },
    },
    rates: {
      carrierInput: 'load-carrier-rate-input',
      carrierNote: 'load-carrier-rate-note',
      carrierSubmit: 'load-carrier-rate-submit',
      shipperInput: 'load-shipper-rate-input',
      shipperNote: 'load-shipper-rate-note',
      shipperSubmit: 'load-shipper-rate-submit',
    },
    requirements: {
      adrCheckbox: 'adr-checkbox',
      dvsCheckbox: 'dvs-checkbox',
      wasteLicenseCheckbox: 'waste-license-checkbox',
    },
    statusBar: {
      advanceAction: 'load-status-dropdown-advance',
      archiveAction: 'load-status-dropdown-archive',
      cancelAction: 'load-status-dropdown-cancel',
      completedWithIssues: 'load-status-dropdown-issues',
      dropdownTrigger: 'load-status-dropdown-trigger',
      revertAction: 'load-status-dropdown-revert',
      status: (alias: string) => `load-status-${alias}`,
    },
    statusHistoryItem: 'load-status-history-item',
    statusSelect: 'load-status-select',
    stops: {
      add: (index: number) => `load-stop-add-${index}`,
      remove: (index: number) => `load-stop-remove-${index}`,
    },
    submitButton: 'submit-button',
    tagTypeDeleteDialog: {
      cancelButton: 'load-tag-type-delete-cancel-button',
      confirmButton: 'load-tag-type-delete-confirm-button',
    },
  },

  // ============================================
  // Location/Stops
  // ============================================
  location: {
    dateTrigger: (index: number) => `date-trigger-${index}`,
    dropDateTrigger: (index: number) => `location-drop-date-${index}`,
    dropTimeSelector: (index: number) => `location-drop-time-${index}`,
    timeInput: (index: number) => `time-input-${index}`,
    timeType: (index: number) => `time-type-${index}`,
    trigger: (index: number) => `location-trigger-${index}`,
    typeCollection: 'location-type-collection',
    typeDelivery: 'location-type-delivery',
  },

  // ============================================
  // Other / Fallback
  // ============================================
  other: {
    details: {
      invoicing: {
        currencySelect: 'other-details-invoicing-currency-select',
      },
    },
  },

  // ============================================
  // Overview Components
  // ============================================
  overview: {
    statusHistoryItem: 'status-history-item',
  },

  // ============================================
  // Pagination
  // ============================================
  pagination: {
    total: 'pagination-total',
  },

  // ============================================
  // Reports
  // ============================================
  report: {
    generate: {
      cancelButton: 'report-generate-cancel-button',
      commissionReportItem: 'report-generate-commission-report-item',
      dateRangeTrigger: 'report-generate-date-range-trigger',
      loadsDataReportItem: 'report-generate-loads-data-report-item',
      menuTrigger: 'report-generate-menu-trigger',
      sheetTitle: 'report-generate-sheet-title',
      submitButton: 'report-generate-submit-button',
    },
    table: {
      actionsTrigger: (id: number) => `report-table-actions-trigger-${id}`,
      downloadItem: 'report-table-download-item',
      statusBadge: (id: number) => `report-table-status-badge-${id}`,
    },
  },
  // ============================================
  // Shipper Management
  // ============================================
  shipper: {
    addCompanyButton: 'shipper-add-company-button',
    details: {
      accountManagerSelect: 'shipper-details-account-manager-select',
      emailInput: 'shipper-details-email-input',
      hubspotInput: 'shipper-details-hubspot-input',
      invoicing: {
        billingAddressInput: 'shipper-details-invoicing-billing-address-input',
        billingAddressSecondLineInput:
          'shipper-details-invoicing-billing-address-second-line-input',
        billingCityInput: 'shipper-details-invoicing-billing-city-input',
        billingPostcodeInput: 'shipper-details-invoicing-billing-postcode-input',
        creditLimitInput: 'shipper-details-invoicing-credit-limit-input',
        currencySelect: 'shipper-details-invoicing-currency-select',
        emailInput: 'shipper-details-invoicing-email-input',
        notesTextarea: 'shipper-details-invoicing-notes-textarea',
        phoneInput: 'shipper-details-invoicing-phone-input',
      },
      notesTextarea: 'shipper-details-notes-textarea',
      websiteInput: 'shipper-details-website-input',
    },
    editCompanyForm: {
      addressInput: 'shipper-edit-company-address-input',
      cityInput: 'shipper-edit-company-city-input',
      countrySelect: 'shipper-edit-company-country-select',
      creditSafeButton: 'shipper-edit-company-creditsafe-button',
      emailInput: 'shipper-edit-company-email-input',
      phoneInput: 'shipper-edit-company-phone-input',
      postcodeInput: 'shipper-edit-company-postcode-input',
      sourceSelect: 'shipper-edit-company-source-select',
      submitButton: 'shipper-edit-company-submit-button',
      titleInput: 'shipper-edit-company-title-input',
      typeSelect: 'shipper-edit-company-type-select',
      vatInput: 'shipper-edit-company-vat-input',
    },
  },

  // ============================================
  // Table Components
  // ============================================
  table: {
    columnSettingsPanel: 'table-column-settings-panel',
    columnSettingsTrigger: 'table-column-settings-trigger',
    footerCell: (columnId: string) => `table-footer-cell-${columnId}`,
    footerRow: 'table-footer-row',
    resizeHandler: (headerId: string) => `${headerId}-resize-handler`,
  },

  // ============================================
  // UI Components (packages/ui)
  // ============================================
  ui: {
    dropdownMenuContent: 'ui-dropdown-menu-content',
    dropdownMenuItem: 'ui-dropdown-menu-item',
    responsiveGrid: {
      '2xl': 'ui-responsive-grid-2xl',
      md: 'ui-responsive-grid-md',
      sm: 'ui-responsive-grid-sm',
    },
  },

  // ============================================
  // User Management
  // ============================================
  user: {
    addButton: 'user-add-button',
    commandInput: 'user-command-input',
    deleteDialog: {
      cancelButton: 'user-delete-cancel-button',
      confirmButton: 'user-delete-confirm-button',
    },
    drawer: {
      closeButton: 'user-drawer-close-button',
      title: 'user-drawer-title',
    },
    form: {
      companyInput: 'user-form-company-input',
      emailInput: 'user-form-email-input',
      lastNameInput: 'user-form-last-name-input',
      localeOption: (slug: string) => `user-form-locale-option-${slug}`,
      localeSelect: 'user-form-locale-select',
      nameInput: 'user-form-name-input',
      passwordInput: 'user-form-password-input',
      phoneInput: 'user-form-phone-input',
      roleOption: (slug: string) => `user-form-role-option-${slug}`,
      roleSelect: 'user-form-role-select',
      sourceOption: (slug: string) => `user-form-source-option-${slug}`,
      sourceSelect: 'user-form-source-select',
      submitButton: 'user-form-submit-button',
    },
    impersonationBanner: 'user-impersonation-banner',
    inputTrigger: 'user-input-trigger',
    table: {
      editButton: 'edit-user-button',
    },
  },
} as const

// Type helper for dynamic test IDs
export type TestId = string
